import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import type { RegistryAsset } from '@/shared/api/transcriptor/registryApi';
import { Button } from '@/shared/ui';

import { useLibraryTemplate } from '../hooks';

import styles from './LibraryTemplate.module.scss';
import { TemplatePreviewContent } from './TemplatePreview';
import { CatalogOriginLabel } from './WorkspaceTemplateCatalog';

type Props = {
  asset: RegistryAsset;
  canAdd: boolean;
  adding: boolean;
  saved: boolean;
  onAdd: () => void;
  onOpen: () => void;
};

// A library template, read-only: the clinic adds it to its templates to use
// and edit it, or opens the copy it already has.
export const LibraryTemplate: FC<Props> = ({
  asset,
  canAdd,
  adding,
  saved,
  onAdd,
  onOpen,
}) => {
  const { template, loading, failed } = useLibraryTemplate(asset);

  return (
    <section
      className={styles.library}
      data-component="workspace-library-template"
    >
      <div className={styles.head}>
        <div className={styles.meta}>
          <h2 className={cn('h5', styles.name)}>{asset.name}</h2>
          <span className={cn('p4', styles.caption)}>
            <CatalogOriginLabel
              origin={
                asset.originKind === 'community' ? 'community' : 'official'
              }
            />{' '}
            ·{' '}
            <FormattedMessage
              id="workspace.template.sectionCount"
              defaultMessage="{count, plural, one {# section} other {# sections}}"
              values={{ count: asset.sectionsCount ?? 0 }}
            />
          </span>
          {asset.description && (
            <p className={cn('p3', styles.description)}>{asset.description}</p>
          )}
        </div>
        {saved ? (
          <Button
            variant="secondary"
            size="small"
            data-component="workspace-library-open"
            onClick={onOpen}
          >
            <FormattedMessage
              id="workspace.templates.openMine"
              defaultMessage="Open saved template"
            />
          </Button>
        ) : (
          <Button
            size="small"
            loading={adding}
            disabled={!canAdd}
            data-component="workspace-library-add"
            onClick={onAdd}
          >
            <FormattedMessage
              id="workspace.templates.addToMine"
              defaultMessage="Save template"
            />
          </Button>
        )}
      </div>
      {template && <TemplatePreviewContent template={template} />}
      {loading && (
        <p className={cn('p3', styles.caption)}>
          <FormattedMessage id="global.loading" defaultMessage="Loading…" />
        </p>
      )}
      {failed && (
        <p className={cn('p3', styles.caption)} role="alert">
          <FormattedMessage
            id="workspace.templates.libraryPreviewFailed"
            defaultMessage="The template could not be loaded."
          />
        </p>
      )}
    </section>
  );
};
